import { P } from "@/components/ui/Heading";
import { Separator } from "@/components/ui/Separator";
import { UpdateAction } from "@/enums/common.enum";
import { SearchLocation } from "@/interfaces/property.interface";
import { useTranslations } from "next-intl";
import { components, MenuListProps } from "react-select";
import { DropdownGroup, DropdownOption } from "./interface";
import { SearchOption } from "./SearchOption";
import { SelectedOption } from "./SelectedOption";

export interface CustomMenuListProps
  extends MenuListProps<DropdownOption, true, DropdownGroup> {
  searchText: string;
  debouncedSearchText: string;
  isInitialized: boolean;
  updateSearchedLocations: (
    location: SearchLocation,
    action: UpdateAction
  ) => void;
  updateRecentlySearchedLocations: (option: DropdownOption) => void;
  isLoading: boolean;
}

export const CustomMenuList: React.FC<CustomMenuListProps> = ({
  options,
  selectOption,
  getValue,
  isLoading,
  searchText,
  debouncedSearchText,
  isInitialized,
  updateSearchedLocations,
  updateRecentlySearchedLocations,
  ...props
}) => {
  const t = useTranslations("shared");
  const selectedValues = getValue() as Array<DropdownOption & SearchLocation>;

  const handleOptionClick = (option: DropdownOption & SearchLocation) => {
    selectOption(option);
    updateRecentlySearchedLocations(option);
  };

  if (!isInitialized) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="p-2">
        <P>{t("words.loading")}</P>
      </div>
    );
  }

  if (
    options.length === 0 &&
    debouncedSearchText.length > 0 &&
    debouncedSearchText === searchText
  ) {
    return (
      <div className="p-2 text-gray-800">
        <P>
          We can&apos;t find any <b>{searchText}</b>. Please check location or
          try with different keywords.
        </P>
      </div>
    );
  }

  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <components.MenuList {...(props as any)} className="flex flex-col gap-2">
      {selectedValues.length > 0 && (
        <>
          <div className="flex flex-wrap gap-1 mt-1">
            {selectedValues.map((value) => (
              <SelectedOption
                key={value.value}
                option={value}
                onRemove={(option) =>
                  updateSearchedLocations(option, UpdateAction.REMOVE)
                }
              />
            ))}
          </div>
          <Separator />
        </>
      )}

      {options.map((groupOrOption) => {
        if ((groupOrOption as DropdownGroup).options) {
          const group = groupOrOption as DropdownGroup;
          return (
            <div key={group.label} className="space-y-2">
              <P className="px-2 font-semibold text-base">{group.label}</P>
              <Separator />
              {group.options.map((option) => (
                <SearchOption
                  key={option.value}
                  option={option}
                  isSelected={selectedValues.some(
                    (selected) => selected.value === option.value
                  )}
                  onSelect={handleOptionClick}
                />
              ))}
            </div>
          );
        }

        const option = groupOrOption as DropdownOption;
        return (
          <SearchOption
            key={option.value}
            option={option}
            isSelected={selectedValues.some(
              (selected) => selected.value === option.value
            )}
            onSelect={handleOptionClick}
          />
        );
      })}
    </components.MenuList>
  );
};
